import {
    Checkbox,
    Colors,
    DraggableList,
    DraggableListItem,
    Icon,
    Text,
    type DraggableItem,
} from '@amelio/ui-library';

import QuestionDraggableOption from '../QuestionDraggableOption/QuestionDraggableOption';

import type { TranslationKey } from '@/i18n-key';
import type { RankingOption } from '@/types/rankingOptionType';

import { useAmelioTranslations } from '@/hooks';

const DEFAULT_TRANSLATION_KEYS = {
    TITLE: 'evaluation360Questionnaire.questionForm.questions.multipleChoice.longTitle',
    DESCRIPTION: 'evaluation360Questionnaire.questionForm.questions.multipleChoice.description',
} as const;

const DEFAULT_TEST_ID_PREFIX = 'checkbox-option';

type RankingOptionListProps = {
    options: DraggableItem<RankingOption>[];
    setOptions: (opts: DraggableItem<RankingOption>[]) => void;
    updateOption: (id: string, value: string) => void;
    deleteOption: (id: string) => void;
    addOption: (index: number) => void;
    handleOtherChange: (checked: boolean) => void;
    isOtherOption: boolean;
    titleKey?: TranslationKey;
    descriptionKey?: TranslationKey;
    dataTestIdPrefix?: string;
    showOtherOption?: boolean;
    showOptionLabel?: boolean;
    optionLabelKey?: TranslationKey;
    gap?: number;
    dataTestId?: string;
};
const RankingOptionList = ({
    addOption,
    deleteOption,
    handleOtherChange,
    options,
    setOptions,
    updateOption,
    isOtherOption,
    titleKey = DEFAULT_TRANSLATION_KEYS.TITLE,
    descriptionKey = DEFAULT_TRANSLATION_KEYS.DESCRIPTION,
    dataTestIdPrefix = DEFAULT_TEST_ID_PREFIX,
    showOtherOption = true,
    showOptionLabel = false,
    optionLabelKey,
    gap,
    dataTestId,
}: RankingOptionListProps) => {
    const { t } = useAmelioTranslations();

    return (
        <div data-testid={dataTestId}>
            <Text
                className={'mt-8'}
                weight={'bold'}
            >
                {t(titleKey)}
            </Text>

            <Text
                className={'mt-2'}
                weight={'light'}
                size={'small'}
            >
                {t(descriptionKey)}
            </Text>

            <div className={'mt-6 max-w-80'}>
                <DraggableList
                    items={options}
                    onReorder={setOptions}
                    gap={gap}
                    useDragHandle
                    renderItem={(item, itemMeta) => (
                        <DraggableListItem isDragging={itemMeta.isDragging}>
                            <QuestionDraggableOption
                                value={item.data.label}
                                dataTestId={`${dataTestIdPrefix}-${itemMeta.index}`}
                                onChange={(value) => updateOption(item.id, value)}
                                onRemove={() => deleteOption(item.id)}
                                canAddNewOption
                                inputWidth={'qdo-input'}
                                isLastOption={itemMeta.index === options.length - 1}
                                onAdd={() => addOption(itemMeta.index)}
                                label={
                                    showOptionLabel && optionLabelKey
                                        ? t(optionLabelKey)
                                        : undefined
                                }
                            />
                        </DraggableListItem>
                    )}
                />

                {options.length === 0 && (
                    <button
                        type={'button'}
                        onClick={() => addOption(0)}
                        className={'cursor-pointer'}
                        aria-label={'Add option'}
                        data-testid={`${dataTestIdPrefix}-empty-add`}
                    >
                        <Icon
                            name={'essential-add-circle'}
                            className={'h-2 w-2'}
                            color={Colors.BASE}
                        />
                    </button>
                )}

                {showOtherOption && (
                    <div className={'mt-4'}>
                        <Checkbox
                            dataTestId={'other-option-checkbox'}
                            label={t(
                                'evaluation360Questionnaire.questionForm.questions.common.otherOptionLabel',
                            )}
                            checked={isOtherOption}
                            onChange={(checked: boolean) => handleOtherChange(checked)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export { RankingOptionList };
export default RankingOptionList;
